import React, { useState } from 'react';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import DataTable from '@/components/UI/DataTable';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { Brokerage, BrokerAccountPermissionsSnapshot, BrokerCapitalBasisSummary, TradingAccount } from '@einhaus/project314-types';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { capitalizeFirstLetter } from '@einhaus/meteoric-helpers';
import { Button, ConfirmationDialog, FormInput, FormSelect, Modal } from '@/components/UI';
import AddTradingAccountModal from './components/AddTradingAccountModal';
import BrokerAccountApprovals from './components/BrokerAccountApprovals';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useBrokerConnections } from '@/hooks/useBrokerConnections';
import { useTradingAccounts } from '@/hooks/useTradingAccounts';
import { showToast } from '@/components/Layout/Toast';
import { useAuth } from '@/context/AuthContext';
import SupportRestrictedFeatureCard from '@/components/Support/SupportRestrictedFeatureCard';

interface TradingAccountRow extends TradingAccount {
    capitalBasisSummaries: BrokerCapitalBasisSummary[];
    brokerConnectionName: string | null;
    brokerConnectionStatus: string | null;
    brokerageOrderCount: number;
    linkedPortfolioCount: number;
    linkedPortfolioNames: string[];
}

interface BrokerConnectionOption {
    id: number;
    brokerage: Brokerage;
    name: string;
    status: string;
}

const capitalBasisLabels: Record<BrokerCapitalBasisSummary['capitalBasis'], string> = {
    cash: 'Cash',
    cashSecuredPut: 'Cash-Secured Puts',
    nakedEquityPut: 'Margin-Backed Short Puts',
    longOptionPremium: 'Long Option Premium',
    definedRiskOption: 'Defined-Risk Options'
};

function formatCurrency(value?: number | null) {
    if (value === null || value === undefined) return 'N/A';

    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPermissionsSummary(snapshot?: BrokerAccountPermissionsSnapshot | null) {
    if (!snapshot) return 'No broker permissions data';

    const flags = [
        `Margin: ${snapshot.marginMode}`,
        snapshot.optionsApprovalLevel !== null ? `Approval: L${snapshot.optionsApprovalLevel}` : null,
        snapshot.optionsTradingLevel !== null ? `Trading: L${snapshot.optionsTradingLevel}` : null,
        snapshot.maxSupportedStrategyClass ? `Max: ${snapshot.maxSupportedStrategyClass}` : null,
        snapshot.isPatternDayTrader === true ? 'Day-trader flag' : null
    ].filter(Boolean);

    return flags.length ? flags.join(' | ') : 'No broker permissions data';
}

function formatCapitalFacts(account: TradingAccountRow) {
    const snapshot = account.brokerCapitalSnapshot;

    if (!snapshot) return 'No broker capital snapshot';

    const facts = [
        snapshot.availableFunds !== null ? `Avail: ${formatCurrency(snapshot.availableFunds)}` : null,
        snapshot.dayTradingBuyingPower !== null ? `DTBP: ${formatCurrency(snapshot.dayTradingBuyingPower)}` : null,
        snapshot.maintenanceRequirement !== null ? `Maint: ${formatCurrency(snapshot.maintenanceRequirement)}` : null,
        snapshot.buyingPowerNonMarginableTrade !== null ? `Non-margin BP: ${formatCurrency(snapshot.buyingPowerNonMarginableTrade)}` : null
    ].filter(Boolean);

    return facts.length ? facts.join(' | ') : 'No extra broker capital data';
}

function formatCapitalBasisSummaries(summaries: BrokerCapitalBasisSummary[]) {
    const nonZeroSummaries = summaries.filter((summary) => summary.allocatedAmount > 0 || summary.availableToAllocate !== null);

    if (!nonZeroSummaries.length) return 'No basis allocations yet';

    return nonZeroSummaries
        .map((summary) => {
            const label = capitalBasisLabels[summary.capitalBasis];
            const allocated = formatCurrency(summary.allocatedAmount);
            const available = summary.availableToAllocate !== null ? formatCurrency(summary.availableToAllocate) : 'N/A';
            const overAllocated = summary.overAllocatedAmount > 0 ? `, ${formatCurrency(summary.overAllocatedAmount)} over capacity` : '';

            return `${label}: ${allocated} allocated, ${available} free${overAllocated}`;
        })
        .join(' | ');
}

const TradingAccounts: React.FC = () => {
    const [error, setError] = useState<string | null>(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
    const [editingAccount, setEditingAccount] = useState<TradingAccountRow | null>(null);
    const [editingAccountName, setEditingAccountName] = useState('');
    const [selectedBrokerConnectionId, setSelectedBrokerConnectionId] = useState<number | null>(null);
    const [pendingDeleteAccount, setPendingDeleteAccount] = useState<TradingAccountRow | null>(null);

    const { sessionPolicy } = useAuth();
    const queryClient = useQueryClient();
    const { canManageBrokerConnections } = sessionPolicy;

    const {
        isLoading,
        query: accountsQuery,
        tradingAccounts
    } = useTradingAccounts({
        enabled: canManageBrokerConnections,
        errorConfig: {
            fallbackErrorMessage: 'Failed to load trading accounts',
            showErrorToast: true,
            setError: (message) => setError(message)
        }
    });

    const { brokerConnections: allBrokerConnectionRows, query: brokerConnectionsQuery } = useBrokerConnections({
        enabled: canManageBrokerConnections,
        errorConfig: {
            fallbackErrorMessage: 'Failed to load broker connections',
            showErrorToast: false,
            setError: (message) => console.error(message)
        }
    });

    const updateTradingAccountMutation = useTrpcMutation(
        trpc.user.updateTradingAccount.mutationOptions({
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchTradingAccounts.queryOptions().queryKey
                });

                await queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchBrokerConnections.queryOptions().queryKey
                });

                await queryClient.invalidateQueries({ queryKey: trpc.user.listBrokerAccountApprovals.pathKey() });
                await queryClient.invalidateQueries({ queryKey: trpc.admin.listBrokerAccountApprovals.pathKey() });
                handleCloseEditModal();
            }
        }),
        {
            successMessage: 'Trading account updated',
            fallbackErrorMessage: 'Failed to update trading account'
        }
    );

    const deleteTradingAccountMutation = useTrpcMutation(
        trpc.user.deleteTradingAccount.mutationOptions({
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchTradingAccounts.queryOptions().queryKey
                });

                await queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchBrokerConnections.queryOptions().queryKey
                });

                await queryClient.invalidateQueries({ queryKey: trpc.user.listBrokerAccountApprovals.pathKey() });
                await queryClient.invalidateQueries({ queryKey: trpc.admin.listBrokerAccountApprovals.pathKey() });
                setPendingDeleteAccount(null);
            }
        }),
        {
            successMessage: 'Trading account removed',
            fallbackErrorMessage: 'Failed to remove trading account'
        }
    );

    const accounts: TradingAccountRow[] = tradingAccounts;
    const allBrokerConnections: BrokerConnectionOption[] = allBrokerConnectionRows;
    const brokerConnections = allBrokerConnections.filter((brokerConnection) => brokerConnection.status !== 'disconnected');

    const connectedServices = Array.from(
        new Set(
            brokerConnections
                .filter((brokerConnection) => brokerConnection.status === 'active')
                .map((brokerConnection) => brokerConnection.brokerage)
        )
    );

    const editableBrokerConnections = editingAccount
        ? allBrokerConnections.filter(
              (brokerConnection) =>
                  brokerConnection.brokerage === editingAccount.brokerage &&
                  (brokerConnection.status === 'active' || brokerConnection.id === editingAccount.brokerConnectionId)
          )
        : [];

    const effectiveSelectedBrokerConnectionId = editableBrokerConnections.some(
        (brokerConnection) => brokerConnection.id === selectedBrokerConnectionId
    )
        ? selectedBrokerConnectionId
        : (editableBrokerConnections[0]?.id ?? null);

    const editableBrokerConnectionOptions = editableBrokerConnections.map((brokerConnection) => ({
        value: brokerConnection.id.toString(),
        label: brokerConnection.status === 'active' ? brokerConnection.name : `${brokerConnection.name} (${brokerConnection.status})`
    }));

    if (!canManageBrokerConnections) {
        return (
            <UserLayout title="Trading Accounts - Project 314">
                <SupportRestrictedFeatureCard
                    description="Trading-account management is disabled during a read-only support session because it can import, relink, or remove brokerage-backed accounts."
                    title="Trading Accounts Are Unavailable"
                />
            </UserLayout>
        );
    }

    const handleOpenAddModal = () => setIsAddModalOpen(true);
    const handleCloseAddModal = () => setIsAddModalOpen(false);

    const handleAddAccount = () => {
        void accountsQuery.refetch();
        void brokerConnectionsQuery.refetch();
    };

    const handleEditAccount = (account: TradingAccountRow) => {
        setEditingAccount(account);
        setEditingAccountName(account.name);
        setSelectedBrokerConnectionId(account.brokerConnectionId);
    };

    const handleCloseEditModal = () => {
        setEditingAccount(null);
        setEditingAccountName('');
        setSelectedBrokerConnectionId(null);
    };

    const handleSaveAccount = () => {
        if (!editingAccount) return;

        if (!effectiveSelectedBrokerConnectionId) {
            showToast(`No usable ${editingAccount.brokerage} connection is available for this trading account.`, 'error');
            return;
        }

        updateTradingAccountMutation.mutate({
            id: editingAccount.id,
            name: editingAccountName.trim(),
            brokerConnectionId: effectiveSelectedBrokerConnectionId
        });
    };

    const handleDeleteAccount = (account: TradingAccountRow) => {
        if (account.linkedPortfolioCount > 0) {
            showToast('Unassign linked portfolios before removing this trading account.', 'error');
            return;
        }

        if (account.brokerageOrderCount > 0) {
            showToast('Trading accounts with brokerage order history cannot be removed.', 'error');
            return;
        }

        setPendingDeleteAccount(account);
    };

    const confirmDeleteAccount = () => {
        if (!pendingDeleteAccount) return;

        deleteTradingAccountMutation.mutate({
            id: pendingDeleteAccount.id
        });
    };

    const columns: ColumnDef<TradingAccountRow>[] = [
        {
            accessorKey: 'id',
            header: 'ID',
            minSize: 60,
            size: 80,
            maxSize: 100
        },
        {
            accessorKey: 'name',
            header: 'Name',
            minSize: 140,
            size: 180,
            maxSize: 260
        },
        {
            accessorKey: 'brokerage',
            header: 'Brokerage',
            minSize: 100,
            size: 120,
            maxSize: 180
        },
        {
            id: 'brokerConnection',
            header: 'Connection',
            accessorFn: (account) =>
                account.brokerConnectionName
                    ? account.brokerConnectionStatus && account.brokerConnectionStatus !== 'active'
                        ? `${account.brokerConnectionName} (${account.brokerConnectionStatus})`
                        : account.brokerConnectionName
                    : 'Unassigned',
            minSize: 160,
            size: 220,
            maxSize: 320
        },
        {
            id: 'displayAccountId',
            header: 'Account ID',
            accessorFn: (account) => account.displayAccountId ?? account.providerAccountId,
            minSize: 120,
            size: 160,
            maxSize: 220
        },
        {
            accessorKey: 'type',
            header: 'Type',
            cell: (info) => capitalizeFirstLetter(info.getValue<string>()),
            minSize: 80,
            size: 100,
            maxSize: 150
        },
        {
            accessorKey: 'accountType',
            header: 'Account Type',
            minSize: 100,
            size: 150,
            maxSize: 200
        },
        {
            id: 'linkedPortfolios',
            header: 'Linked Portfolios',
            cell: ({ row }) =>
                row.original.linkedPortfolioCount
                    ? `${row.original.linkedPortfolioCount}: ${row.original.linkedPortfolioNames.join(', ')}`
                    : '0',
            minSize: 180,
            size: 240,
            maxSize: 360
        },
        {
            accessorKey: 'brokerageOrderCount',
            header: 'Order History',
            minSize: 100,
            size: 120,
            maxSize: 160
        },
        {
            accessorKey: 'equity',
            header: 'Equity',
            cell: (info) => formatCurrency(info.getValue<number>()),
            minSize: 100,
            size: 150,
            maxSize: 200
        },
        {
            accessorKey: 'balanceLastSyncedDateTime',
            header: 'Balance As Of',
            cell: (info) => info.getValue<string | null>() ?? 'Not synced',
            minSize: 150,
            size: 180,
            maxSize: 240
        },
        {
            accessorKey: 'cashBalance',
            header: 'Cash Balance',
            cell: (info) => formatCurrency(info.getValue<number>()),
            minSize: 100,
            size: 150,
            maxSize: 200
        },
        {
            accessorKey: 'buyingPower',
            header: 'Buying Power',
            cell: (info) => formatCurrency(info.getValue<number>()),
            minSize: 100,
            size: 150,
            maxSize: 200
        },
        {
            id: 'optionsBuyingPower',
            header: 'Options BP',
            accessorFn: (account) => account.brokerCapitalSnapshot?.optionsBuyingPower ?? account.optionsBuyingPower ?? null,
            cell: (info) => formatCurrency(info.getValue<number | null>()),
            minSize: 110,
            size: 150,
            maxSize: 210
        },
        {
            id: 'brokerPermissions',
            header: 'Permissions',
            accessorFn: (account) => formatPermissionsSummary(account.brokerPermissionsSnapshot),
            minSize: 260,
            size: 340,
            maxSize: 460
        },
        {
            id: 'brokerCapitalFacts',
            header: 'Broker Facts',
            accessorFn: (account) => formatCapitalFacts(account),
            minSize: 260,
            size: 360,
            maxSize: 520
        },
        {
            accessorKey: 'allocatedAmount',
            header: 'Cash Allocated',
            cell: (info) => formatCurrency(info.getValue<number>()),
            minSize: 120,
            size: 160,
            maxSize: 220
        },
        {
            id: 'capitalBasisSummaries',
            header: 'Capital Buckets',
            accessorFn: (account) => formatCapitalBasisSummaries(account.capitalBasisSummaries),
            minSize: 280,
            size: 420,
            maxSize: 560
        },
        {
            accessorKey: 'addedDateTime',
            header: 'Added Date',
            cell: (info) => info.getValue<string>().split(' ')[0],
            minSize: 100,
            size: 130,
            maxSize: 180
        },
        {
            header: 'Actions',
            id: 'actions',
            cell: ({ row }) => {
                const account = row.original;
                const isDeleteBlocked = account.linkedPortfolioCount > 0 || account.brokerageOrderCount > 0;

                const deleteBlockerMessage =
                    account.linkedPortfolioCount > 0
                        ? 'Unassign linked portfolios first'
                        : account.brokerageOrderCount > 0
                          ? 'Has brokerage order history'
                          : null;

                return (
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                            <Button size="sm" variant="secondary" onClick={() => handleEditAccount(account)}>
                                Edit
                            </Button>
                            <Button disabled={isDeleteBlocked} size="sm" variant="danger" onClick={() => handleDeleteAccount(account)}>
                                Remove
                            </Button>
                        </div>
                        {deleteBlockerMessage && <div className="text-xs text-zinc-400">{deleteBlockerMessage}</div>}
                    </div>
                );
            },
            minSize: 180,
            size: 220,
            maxSize: 280
        }
    ];

    if (isLoading) {
        return (
            <UserLayout title="Trading Accounts - Project 314">
                <DataLoadingSpinner text="Loading trading accounts..." />
            </UserLayout>
        );
    }

    if (error) {
        return (
            <UserLayout title="Trading Accounts - Error - Project 314">
                <div className="container mx-auto">
                    <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">{error}</div>
                </div>
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1600px" title="Trading Accounts - Project 314">
            <div className="container mx-auto px-4">
                <div className="flex justify-between items-center mt-4 mb-4">
                    <div>
                        <h3 className="text-white text-xl font-semibold">Trading Accounts</h3>
                    </div>
                    <div>
                        <Button variant="primary" onClick={handleOpenAddModal}>
                            Add Trading Account
                        </Button>
                    </div>
                </div>

                <DataTable
                    allowOverflow
                    search
                    sort
                    striped
                    columns={columns}
                    data={accounts}
                    pagination={{ enabled: true, pageSize: 20 }}
                />
                <BrokerAccountApprovals />

                <AddTradingAccountModal
                    connectedServices={connectedServices}
                    isOpen={isAddModalOpen}
                    onAdd={handleAddAccount}
                    onClose={handleCloseAddModal}
                />

                <Modal
                    footer={
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" onClick={handleCloseEditModal}>
                                Cancel
                            </Button>
                            <Button
                                disabled={
                                    !editingAccountName.trim() ||
                                    !effectiveSelectedBrokerConnectionId ||
                                    updateTradingAccountMutation.isPending
                                }
                                variant="primary"
                                onClick={handleSaveAccount}
                            >
                                Save Changes
                            </Button>
                        </div>
                    }
                    isOpen={editingAccount !== null}
                    size="lg"
                    title="Edit Trading Account"
                    onClose={handleCloseEditModal}
                >
                    {editingAccount && (
                        <div className="space-y-4">
                            <FormInput
                                id="tradingAccountName"
                                label="Account Name"
                                value={editingAccountName}
                                onChange={(event) => setEditingAccountName(event.target.value)}
                            />

                            <FormSelect
                                id="tradingAccountBrokerConnection"
                                label="Broker Connection"
                                options={editableBrokerConnectionOptions}
                                value={effectiveSelectedBrokerConnectionId?.toString() ?? ''}
                                onChange={(event) => setSelectedBrokerConnectionId(Number(event.target.value))}
                            />

                            {!editableBrokerConnectionOptions.length && (
                                <div className="text-sm text-red-300">
                                    No usable {editingAccount.brokerage} broker connection is available for this account.
                                </div>
                            )}
                        </div>
                    )}
                </Modal>

                <ConfirmationDialog
                    confirmText="Remove"
                    confirmVariant="danger"
                    isConfirmLoading={deleteTradingAccountMutation.isPending}
                    isOpen={pendingDeleteAccount !== null}
                    message={
                        pendingDeleteAccount
                            ? `Remove trading account "${pendingDeleteAccount.name}"? This only works for imported accounts with no linked portfolios and no brokerage order history.`
                            : ''
                    }
                    title="Remove Trading Account"
                    onCancel={() => setPendingDeleteAccount(null)}
                    onConfirm={confirmDeleteAccount}
                />
            </div>
        </UserLayout>
    );
};

export default TradingAccounts;
